/**
 * Drag image shaped like the AI chat's file chips, so what the user drags already looks like the tag
 * it becomes on drop. Shared by the Files table rows, the Results viewer tabs and the Jobs rows.
 */

export type DragTagVariant = 'file' | 'folder' | 'result' | 'job'

interface DragTagStyle {
  icon: string
  color: string
  background: string
  border: string
}

// Same palette as `.file-tag--file/--folder/--result/--job` in ai-chat-v2.scss.
const STYLES: Record<DragTagVariant, DragTagStyle> = {
  file: {
    icon: 'far fa-file',
    color: '#888e00',
    background: 'rgba(136, 142, 0, 0.1)',
    border: '1px solid rgba(136, 142, 0, 0.3)',
  },
  folder: {
    icon: 'fas fa-folder',
    color: '#6c757d',
    background: 'rgba(108, 117, 125, 0.1)',
    border: '1px solid rgba(108, 117, 125, 0.3)',
  },
  result: {
    icon: 'fas fa-chart-simple',
    color: '#0369a1',
    background: '#e0f2fe',
    border: '1px solid #7dd3fc',
  },
  job: {
    icon: 'fas fa-gears',
    color: '#854d0e',
    background: '#fef9c3',
    border: '1px solid #fde047',
  },
}

function buildPreview(label: string, variant: DragTagVariant): HTMLDivElement {
  const {icon: iconClass, color, background, border} = STYLES[variant]
  const preview = document.createElement('div')
  Object.assign(preview.style, {
    position: 'fixed',
    top: '-1000px',
    left: '-1000px',
    zIndex: '9999',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 6px',
    fontSize: 'var(--ob-font-size-xs)',
    fontWeight: '500',
    borderRadius: '4px',
    maxWidth: '240px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    border,
    background,
    color,
  })

  const icon = document.createElement('i')
  icon.className = iconClass
  Object.assign(icon.style, {fontSize: 'var(--ob-font-size-xs)', lineHeight: '1', flexShrink: '0'})

  const text = document.createElement('span')
  text.textContent = label
  Object.assign(text.style, {overflow: 'hidden', textOverflow: 'ellipsis'})

  preview.appendChild(icon)
  preview.appendChild(text)
  return preview
}

/**
 * Use a chip-styled element as the drag image. Returns the element, which stays in the document
 * until {@link removeDragTagPreview} is called on drag end; `null` if the browser refused it (the
 * default drag image is used then).
 */
export function setDragTagPreview(
  e: Pick<DragEvent, 'dataTransfer'> | React.DragEvent,
  label: string,
  variant: DragTagVariant,
): HTMLDivElement | null {
  const {dataTransfer} = e
  if (!dataTransfer) return null
  const preview = buildPreview(label, variant)
  try {
    document.body.appendChild(preview)
    // Keep the cursor near the chip's left edge so it reads as "carrying" the tag.
    const offsetX = Math.min(120, preview.offsetWidth / 2)
    const offsetY = Math.min(10, preview.offsetHeight / 2)
    dataTransfer.setDragImage(preview, offsetX, offsetY)
    return preview
  } catch {
    preview.remove()
    return null
  }
}

/** Remove a preview created by {@link setDragTagPreview}. Safe to call with `null`. */
export function removeDragTagPreview(preview: HTMLElement | null): void {
  preview?.remove()
}
